import useBottomSheet from 'hooks/common/useBottomSheet';
import { FC, useEffect, useRef, useState, useCallback } from 'react';
import TypeButtons from './components/TypeButtonInfo';
import ProfileInfo from './components/ProfileInfo';
import ProfileImage from './components/ProfileImage';
import FriendAcceptText from './components/FriendAcceptText';
import { BJProfile } from 'types/bjProfile';

export type ProfileType = 'friend' | 'message' | 'tapToMatch' | 'call' | 'friendAccept' | 'prime' | undefined;

export type ProfileData = {
  fbUid: string;
  type: ProfileType;
  category?: string;
  isOnline?: boolean;
  canCall?: boolean;
  friendAcceptText?: { title: string; subtitle: string };
};

const MYINFO_WATCHED_FIELDS = ['specialLocale', 'nick', 'signup_preference'] as const;

const Profile: FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { sheets } = useBottomSheet();
  const sheet = sheets.Profile;
  const data = sheet.data as ProfileData;
  const [userInfo, setUserInfo] = useState<any>(null);
  const [bjProfile, setBjProfile] = useState<BJProfile | null>(null);
  const eventToken = useRef<any>(null);
  const isPrime = data.type === 'prime';

  const loadUserInfo = useCallback(async () => {
    const userInfo = await FriendsDataManager.getUserInfo(data.fbUid);
    setUserInfo(userInfo);
  }, [data.fbUid]);

  useEffect(() => {
    if (!data.fbUid) return;
    loadUserInfo();
  }, [data.fbUid, loadUserInfo]);

  useEffect(() => {
    if (!isPrime || !data.fbUid) return;
    PrimeDataManager.getBJProfile(data.fbUid, (err: string, profile: BJProfile) => {
      if (err) {
        BLog.error('[Profile] getBJProfile(' + data.fbUid + ') failed - ' + err);
        return;
      }
      setBjProfile(profile);
    });
  }, [isPrime, data.fbUid]);

  // 바텀시트에서 데이터 업데이트
  useEffect(() => {
    if (sheet.data.userInfo) {
      setUserInfo(sheet.data.userInfo);
    }
  }, [sheet.data.userInfo]);

  // MYINFO_WATCHED_FIELDS에 있는 필드만 감지하여 반영
  const handleMyInfoChanged = useCallback(() => {
    if (!userInfo || data.fbUid !== myinfo.fbUid) return;

    const hasChange = MYINFO_WATCHED_FIELDS.some((key) => userInfo[key] !== (myinfo as any)[key]);
    if (!hasChange) return;
    
    setUserInfo((prev: any) => {
      if (!prev) return prev;
      const next = { ...prev };
      MYINFO_WATCHED_FIELDS.forEach((key) => {
        next[key] = (myinfo as any)[key];
      });
      return next;
    });
  }, [data.fbUid, userInfo]);

  useEffect(()=> {
    if (!eventToken.current) {
      eventToken.current = MyDataManager.events.on('myInfoChanged', handleMyInfoChanged);
    }

    return () => {
      if (eventToken.current) {
        eventToken.current.remove();
      }
      eventToken.current = null;
    };
  }, [handleMyInfoChanged]);

  return (
    <div ref={ref} className="relative h-full w-full">
      <ProfileImage userInfo={userInfo} category={data.category} isPrime={isPrime} />
      {data.type === 'friendAccept' ? (
        <FriendAcceptText friendAcceptText={data.friendAcceptText} />
      ) : (
        <ProfileInfo userInfo={userInfo} isPrime={isPrime} bjProfile={bjProfile} />
      )}
      <TypeButtons userInfo={userInfo} data={data} bjProfile={bjProfile} />
    </div>
  );
};

export default Profile;
